import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { register as apiRegister } from '../services/api';
import AuthHero from '../components/AuthHero';
import SocialAuthButtons from '../components/SocialAuthButtons';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './Login.css';
import './Register.css';

/**
 * Public registration page - anyone can create an account.
 * New users become managers with verified=false; they can only view the demo menu
 * until an admin verifies them to create restaurants.
 *
 * Shares the marketing-hero + auth-card layout with Login (Login.css classes).
 */
const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.email || !formData.password || !formData.name) {
      setError(t('auth.register.validation.nameRequired'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.register.validation.passwordLength'));
      setLoading(false);
      return;
    }

    try {
      await apiRegister({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined
      });
      setUserEmail(formData.email);
      setRegistrationSuccess(true);
      setSuccessDialogOpen(true);
    } catch (err) {
      const data = err.response?.data;
      if (data?.canResend) {
        setError(data.error || t('auth.errors.emailNotVerified'));
        setShowResendButton(true);
        setUserEmail(formData.email);
      } else {
        setError(data?.error || t('auth.errors.registrationFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page fade-in">
      <AuthHero />

      <div className="register-form-container">
        <div className="register-form-wrapper">
          <div
            className="login-tabs"
            role="tablist"
            aria-label={t('auth.login.tabs', 'Authentication')}
          >
            <Link to="/login" role="tab" aria-selected="false" className="login-tab">
              {t('auth.login.signInTab', 'Sign In')}
            </Link>
            <span role="tab" aria-selected="true" className="login-tab active">
              {t('auth.login.registerTab', 'Register')}
            </span>
          </div>

          <div className="register-header">
            <h1>{t('auth.register.title')}</h1>
            <p>{t('auth.register.subtitle')}</p>
          </div>

          {error && (
            <div className="error-message">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
              {showResendButton && userEmail && (
                <p style={{ marginTop: 8, fontSize: '0.9rem' }}>
                  <Link to="/resend-verification" state={{ email: userEmail }}>
                    {t('auth.verification.resendEmail', 'Resend verification email')}
                  </Link>
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="register-form">
            <div className="form-group">
              <label htmlFor="name">{t('auth.register.nameLabel')}</label>
              <div className="input-wrapper">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <input
                  id="name"
                  type="text"
                  name="name"
                  className="form-input has-icon"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('auth.register.namePlaceholder')}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">{t('auth.register.emailLabel')}</label>
              <div className="input-wrapper">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  id="email"
                  type="email"
                  name="email"
                  className="form-input has-icon"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('auth.register.emailPlaceholder')}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('auth.register.passwordLabel')}</label>
              <div className="input-wrapper">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input has-icon has-toggle"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.register.passwordPlaceholder')}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t('auth.login.hidePassword', 'Hide password') : t('auth.login.showPassword', 'Show password')}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">{t('auth.register.phoneLabel')}</label>
              <div className="input-wrapper">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                </svg>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  className="form-input has-icon"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('auth.register.phonePlaceholder')}
                  autoComplete="tel"
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg w-full login-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading loading-sm"></span>
                  {t('auth.register.submitting')}
                </>
              ) : (
                t('auth.register.submitButton')
              )}
            </button>
          </form>

          <div className="login-divider"><span>{t('auth.login.orContinueWith', 'or continue with')}</span></div>

          <SocialAuthButtons onError={setError} />

          <p className="login-create-account">
            {t('auth.register.hasAccount')} <Link to="/login">{t('auth.register.signIn')}</Link>
          </p>

          <div className="login-meta-row">
            <LanguageSwitcher />
            <span className="login-support">
              {t('auth.login.needHelp', 'Need help?')}{' '}
              <a href="mailto:support@mesoon.app">{t('auth.login.contactSupport', 'Contact support')}</a>
            </span>
          </div>

          <p className="login-copyright">{t('auth.login.copyright', { year: new Date().getFullYear(), defaultValue: '© {{year}} Mesoon. All rights reserved.' })}</p>
        </div>
      </div>

      <Dialog open={successDialogOpen} onClose={() => { setSuccessDialogOpen(false); navigate('/login'); }}>
        <DialogTitle>{registrationSuccess ? t('auth.verification.checkEmail', 'Check Your Email') : t('auth.register.successTitle')}</DialogTitle>
        <DialogContent>
          {registrationSuccess ? (
            <>
              <p>{t('auth.verification.verificationSent', 'We sent a verification link to')}</p>
              <p className="user-email" style={{ fontWeight: 600, margin: '8px 0' }}>{userEmail}</p>
              <p>{t('auth.verification.clickLink', 'Please click the link in the email to verify your account.')}</p>
              <p style={{ marginTop: 16 }}>
                <Link to="/resend-verification" state={{ email: userEmail }} onClick={() => setSuccessDialogOpen(false)}>
                  {t('auth.verification.didntReceive', "Didn't receive the email?")}
                </Link>
              </p>
            </>
          ) : (
            t('auth.register.successMessage')
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSuccessDialogOpen(false); navigate('/login'); }} color="primary" variant="contained">{t('auth.register.signIn')}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Register;
